import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { getAllSessions } from "../../../service/sessions"
import { getAllResultBreakdowns, sortBreakdownsByClass, type ResultBreakdown } from "../../../service/result-breakdown"
import { getAllClasses } from "../../../service/classes"
import { all_routes } from "../../router/all_routes"
import { selectCurrentBranch } from "../../../core/data/redux/slice/branchesSlice"

type SessionOpt = { _id: string; title: string }
type ClassOpt = { _id: string; name: string; sections: { _id: string; name: string }[] }

// Picker → batch sheet, mirroring RMS's ReportCards.jsx: Session → Breakdown
// → Class → Section cascade, then Open/Print render every student in the
// section as one A4 page each, stacked for one print job (see
// ../report-card-sheet). No per-student list here — that's the whole point
// of the batch flow.
const ReportCardPage = () => {
  const currentBranch = useSelector(selectCurrentBranch)
  const [sessions, setSessions] = useState<SessionOpt[]>([])
  const [breakdowns, setBreakdowns] = useState<ResultBreakdown[]>([])
  const [classes, setClasses] = useState<ClassOpt[]>([])
  const [filter, setFilter] = useState({ session: "", breakdown: "", class: "", section: "" })

  useEffect(() => {
    // Scopes to the current campus implicitly via the x-branch header (set
    // from whichever campus is active when this fires). Missing
    // currentBranch?._id in the deps meant switching campus in the top bar
    // AFTER this page loaded left the Session list stale from the OLD
    // campus -- same display text ("Aug 2026-2027" exists per-campus), so
    // picking it silently resolved to the wrong campus's session id while
    // Class/Section correctly used the new one, guaranteeing an empty
    // Section list downstream. Classes already reacts to currentBranch;
    // Sessions needs to as well.
    getAllSessions({ limit: 200, status: "active" }).then((r: any) => setSessions(r?.sessions || [])).catch(() => {})
    // A session already picked before the switch belongs to the OLD campus
    // -- clear the whole cascade rather than leave a stale id selected.
    setFilter({ session: "", breakdown: "", class: "", section: "" })
  }, [currentBranch?._id])

  useEffect(() => {
    // Same fix as Marks Tabulation: without branch, a Class shared across
    // campuses returns every campus's same-named Section mixed together
    // (e.g. "A" showing up 2-3x with no way to tell them apart). Session
    // matters too -- Class.sections accumulates every session's sections
    // over the class's lifetime (never pruned), so last year's "A" and
    // this year's "A" both show unless session narrows it down as well.
    if (!currentBranch?._id || !filter.session) { setClasses([]); return }
    getAllClasses({ limit: 500, branch: currentBranch._id, session: filter.session })
      .then((r: any) => setClasses((r?.classes || []).map((c: any) => ({ _id: c._id, name: c.name, sections: c.sections || [] }))))
      .catch(() => setClasses([]))
  }, [currentBranch?._id, filter.session])

  useEffect(() => {
    if (!filter.session || !currentBranch?._id) { setBreakdowns([]); return }
    // status: "active" excluded every real breakdown -- the usable ones are
    // "submitted" once marks are finalized, not stuck in "active" (same fix
    // as Marks Entry/Marks Tabulation for the identical reason).
    //
    // branchId is required: without it, the backend expands `session` to
    // every campus's own same-titled session ("Aug 2026-2027" exists once
    // per campus) and returns every campus's breakdowns mixed together --
    // same cross-campus leak fixed on Marks Tabulation, same cause.
    getAllResultBreakdowns({ session: filter.session, branchId: currentBranch._id, all: true, limit: 200 })
      .then(r => setBreakdowns(sortBreakdownsByClass(r?.data || [])))
      .catch(() => {})
  }, [filter.session, currentBranch?._id])

  const setF = (k: keyof typeof filter, v: string) => {
    setFilter(s => {
      const next = { ...s, [k]: v }
      const order: (keyof typeof filter)[] = ["session", "breakdown", "class", "section"]
      const i = order.indexOf(k)
      for (let j = i + 1; j < order.length; j++) next[order[j]] = ""
      return next
    })
  }

  const selectedBreakdown = breakdowns.find(b => b._id === filter.breakdown)
  const breakdownClassIds = useMemo(() => new Set((selectedBreakdown?.classes || []).map(c => String(c._id))), [selectedBreakdown])
  const classOptions = useMemo(() => classes.filter(c => breakdownClassIds.size === 0 || breakdownClassIds.has(c._id)), [classes, breakdownClassIds])
  const sectionOptions = useMemo(() => classOptions.find(c => c._id === filter.class)?.sections || [], [classOptions, filter.class])

  const ready = !!(filter.breakdown && filter.class && filter.section)
  const sheetUrl = (print: boolean) => {
    const qp = new URLSearchParams({ breakdown: filter.breakdown, class: filter.class, section: filter.section })
    if (print) qp.set("print", "1")
    return `${all_routes.reportCardSheet}?${qp.toString()}`
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="mb-3">
          <h3 className="page-title mb-1">Report Cards</h3>
          <p className="text-muted mb-0">
            Pick the breakdown × class × section. Open generates one A4 card per student. When a designer
            template exists for the picked class, that layout is used.
          </p>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label className="form-label">Session</label>
                <select className="form-select" value={filter.session} onChange={e => setF("session", e.target.value)}>
                  <option value="">— Select session —</option>
                  {sessions.map(s => <option key={s._id} value={s._id}>{s.title}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Breakdown</label>
                <select className="form-select" value={filter.breakdown} disabled={!filter.session} onChange={e => setF("breakdown", e.target.value)}>
                  <option value="">{!filter.session ? "Pick a session first" : "— Select breakdown —"}</option>
                  {breakdowns.map(b => <option key={b._id} value={b._id}>{b.title}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Class</label>
                <select className="form-select" value={filter.class} disabled={!filter.breakdown} onChange={e => setF("class", e.target.value)}>
                  <option value="">{!filter.breakdown ? "Pick a breakdown first" : "— Select class —"}</option>
                  {classOptions.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div className="col-md-3">
                <label className="form-label">Section</label>
                <select className="form-select" value={filter.section} disabled={!filter.class} onChange={e => setF("section", e.target.value)}>
                  <option value="">{!filter.class ? "Pick a class first" : "— Select section —"}</option>
                  {sectionOptions.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                </select>
                {!ready && <small className="text-muted">Required</small>}
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body d-flex align-items-center justify-content-between flex-wrap gap-2">
            <span className="text-muted">
              {ready
                ? "Cards open in a new tab — one A4 page per student. Print captures the whole section in one job."
                : "Walk through the cascade above to enable Open and Print."}
            </span>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={!ready}
                onClick={() => window.open(sheetUrl(false), "_blank")}
              >
                Open ↗
              </button>
              <button
                className="btn btn-dark btn-sm"
                disabled={!ready}
                onClick={() => window.open(sheetUrl(true), "_blank")}
              >
                Print ↗
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ReportCardPage
